import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const StradivariAppPage = () => {
  return (
    <div className="min-h-screen bg-white text-[#333333]">
      <Helmet>
        <title>StradivariApp | Infinity Cloud</title>
        <meta name="description" content="StradivariApp - L'Intelligence Artificielle Redéfinit l'Expression Musicale" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 pb-16 overflow-hidden relative">
        <div 
          className="absolute inset-0 opacity-10 z-0" 
          style={{
            backgroundImage: `url('/images/stradivari-expression.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(50px)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span 
                style={{
                  background: 'linear-gradient(135deg, #ff9500, #ff2d55)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  paddingBottom: '0.15em',
                  lineHeight: 1.2
                }}
              >
                StradivariApp
              </span>
            </h1>
            <p className="text-xl text-[#666666] max-w-3xl mx-auto">
              L'Intelligence Artificielle qui comprend votre intention musicale et la traduit en expression nuancée
            </p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-12 flex justify-center"
            >
              <img 
                src="/images/stradivari-violin-ai.png" 
                alt="StradivariApp Violin AI" 
                className="w-full max-w-2xl rounded-2xl shadow-2xl"
                style={{ 
                  animation: 'float 6s ease-in-out infinite',
                  maxWidth: '80%'
                }}
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Concept */}
      <section className="py-16 bg-[#fffcf5]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #ff9500, #ff2d55)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Paradigme de l'Intention
            </span>
            <h2 className="text-4xl font-semibold mb-6 text-[#333333]">
              De l'Intention à l'Expression
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-[#666666]">
              Décrivez votre intention musicale en langage naturel, StradivariApp s'occupe de la traduire en paramètres techniques précis
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex-1 order-2 md:order-1"
            >
              <h3 className="text-2xl font-semibold mb-4">Libérez votre créativité</h3>
              <p className="text-[#666666] mb-6">
                Oubliez les centaines de paramètres MIDI et les keyswitches complexes. Concentrez-vous sur l'émotion et l'expressivité que vous souhaitez transmettre, StradivariApp gère la complexité technique pour vous.
              </p>
              
              <h3 className="text-2xl font-semibold mb-4">Interface Intuitive</h3>
              <p className="text-[#666666]">
                Notre IA conversationnelle spécialisée comprend les nuances du langage musical, vous permettant d'exprimer des intentions subtiles comme "un peu plus mélancolique" ou "avec une attaque plus vive".
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex-1 order-1 md:order-2 flex justify-center"
            >
              <img 
                src="/images/stradivari-translation.png" 
                alt="Traduction Intention-Paramètres" 
                className="rounded-2xl shadow-lg w-full max-w-md"
                style={{ animation: 'float 6s ease-in-out infinite 1s' }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Highlights */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #ff9500, #ff2d55)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Technologie
            </span>
            <h2 className="text-4xl font-semibold mb-6 text-[#333333]">
              L'IA au Service de la Musique
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-[#666666]">
              Des innovations de pointe pour une expression musicale sans précédent
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-[24px] shadow-lg p-6 hover:shadow-xl transition-shadow"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <div 
                className="w-12 h-12 flex items-center justify-center rounded-full mb-4"
                style={{ background: 'linear-gradient(135deg, #ff9500, #ff2d55)' }}
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 5.523-4.477 10-10 10S1 17.523 1 12 5.477 2 11 2s10 4.477 10 10z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">IA Conversationnelle Spécialisée</h3>
              <p className="text-[#666666]">
                Modèle entraîné sur 10M+ d'exemples musicaux pour comprendre les nuances de votre intention, surpassant les IA génériques de 43%.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-[24px] shadow-lg p-6 hover:shadow-xl transition-shadow"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <div 
                className="w-12 h-12 flex items-center justify-center rounded-full mb-4"
                style={{ background: 'linear-gradient(135deg, #ff9500, #ff2d55)' }}
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Dictionnaire Expressif Stradivari</h3>
              <p className="text-[#666666]">
                Base de données propriétaire de 5000+ mappings entre intentions et paramètres techniques, couvrant articulations, techniques et émotions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-[24px] shadow-lg p-6 hover:shadow-xl transition-shadow"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <div 
                className="w-12 h-12 flex items-center justify-center rounded-full mb-4"
                style={{ background: 'linear-gradient(135deg, #ff9500, #ff2d55)' }}
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Traduction Paramétrique Temps Réel</h3>
              <p className="text-[#666666]">
                Génération de commandes MIDI complexes (keyswitches, CC, courbes) avec une latence &lt; 20ms pour une utilisation live fluide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact & Benefits */}
      <section className="py-16 bg-[#fffcf5]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #ff9500, #ff2d55)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              className="text-xl font-medium inline-block mb-3"
            >
              Impact
            </span>
            <h2 className="text-4xl font-semibold mb-6 text-[#333333]">
              Révolutionner la Création Musicale
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-[#666666]">
              Des résultats concrets qui transforment le flux de travail des musiciens et producteurs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-[24px] shadow-lg p-6 hover:shadow-xl transition-shadow"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 className="text-xl font-semibold mb-3">Gain de Temps Spectaculaire</h3>
              <p className="text-[#666666]">
                Réduction de 87% du temps nécessaire pour obtenir des performances musicales expressives, passant d'une journée à 30 minutes pour une maquette orchestrale complexe.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-[24px] shadow-lg p-6 hover:shadow-xl transition-shadow"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 className="text-xl font-semibold mb-3">Fidélité Expressive Accrue</h3>
              <p className="text-[#666666]">
                Augmentation de 92% de la satisfaction des utilisateurs concernant la capacité à traduire fidèlement leur intention musicale en résultat sonore.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-[24px] shadow-lg p-6 hover:shadow-xl transition-shadow"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 className="text-xl font-semibold mb-3">Démocratisation de l'Expression</h3>
              <p className="text-[#666666]">
                Permet aux créateurs de tous niveaux d'atteindre un niveau d'expressivité auparavant réservé aux experts techniques, élargissant l'accès à la création musicale nuancée.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-[24px] shadow-lg p-6 hover:shadow-xl transition-shadow"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 className="text-xl font-semibold mb-3">Outil Pédagogique Puissant</h3>
              <p className="text-[#666666]">
                Amélioration de 38% de la compréhension de l'interprétation musicale chez les étudiants, en facilitant l'exploration des liens entre intention et réalisation sonore.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call To Action */}
      <section className="py-16 bg-gradient-to-r from-[#ff9500] to-[#ff2d55] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-6">Entrez dans la nouvelle ère de l'expression musicale</h2>
            <p className="max-w-2xl mx-auto text-lg mb-8 text-white/90">
              Découvrez comment StradivariApp peut libérer votre potentiel créatif et transformer votre manière de faire de la musique.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-orange-600 font-semibold py-3 px-8 rounded-full text-lg shadow-lg"
            >
              Demander une démonstration
            </motion.button>
          </motion.div>
        </div>
      </section>

      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
        `}
      </style>
    </div>
  );
};

export default StradivariAppPage; 